import { lazy, Suspense } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ApiErrorState } from "../shared/ApiErrorState";
import { useTrafficSummary } from "./hooks/useTrafficSummary";

const TrafficCharts = lazy(() =>
  import("./TrafficCharts").then((module) => ({ default: module.TrafficCharts })),
);

export const TrafficView = () => {
  const { sinceHours, setSinceHours, summary, loading, error, periods } = useTrafficSummary();

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <ApiErrorState message={error} variant="alert" />
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{ py: 2 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
        <Typography variant="h5" fontWeight="bold" sx={{ flex: 1 }}>
          Sivuliikenne
        </Typography>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="traffic-period-label">Aikaväli</InputLabel>
          <Select
            labelId="traffic-period-label"
            label="Aikaväli"
            value={sinceHours}
            onChange={(e) => {
              setSinceHours(Number(e.target.value));
            }}
          >
            {periods.map((period) => (
              <MenuItem key={period.hours} value={period.hours}>
                {period.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Typography color="text.secondary">
        Yhteensä {summary?.totalViews.toLocaleString("fi-FI") ?? "0"} sivulatausta ja{" "}
        {summary?.totalUniqueVisitors.toLocaleString("fi-FI") ?? "0"} arvioitua uniikkia kävijää
        valitulla aikavälillä. Uniikit lasketaan palvelimella päiväkohtaisella tiivistelmällä ilman
        evästeitä. Siirtymät vaativat evästeiden hyväksynnän.
      </Typography>

      {summary ? (
        <Suspense
          fallback={
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          }
        >
          <TrafficCharts summary={summary} />
        </Suspense>
      ) : null}

      <Typography variant="h6" fontWeight="bold">
        Suosituimmat sivut
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Sivu</TableCell>
            <TableCell align="right">Käynnit</TableCell>
            <TableCell align="right">Uniikit</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(summary?.pages ?? []).map((row) => (
            <TableRow key={row.path}>
              <TableCell sx={{ fontFamily: "monospace" }}>{row.path}</TableCell>
              <TableCell align="right">{row.count.toLocaleString("fi-FI")}</TableCell>
              <TableCell align="right">{row.uniqueVisitors.toLocaleString("fi-FI")}</TableCell>
            </TableRow>
          ))}
          {(summary?.pages.length ?? 0) === 0 && (
            <TableRow>
              <TableCell colSpan={3}>
                <Typography color="text.secondary">Ei dataa vielä.</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Typography variant="h6" fontWeight="bold" sx={{ pt: 2 }}>
        Yleisimmät siirtymät
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Mistä</TableCell>
            <TableCell>Mihin</TableCell>
            <TableCell align="right">Kerrat</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(summary?.transitions ?? []).map((row) => (
            <TableRow key={`${row.fromPath}->${row.toPath}`}>
              <TableCell sx={{ fontFamily: "monospace" }}>{row.fromPath}</TableCell>
              <TableCell sx={{ fontFamily: "monospace" }}>{row.toPath}</TableCell>
              <TableCell align="right">{row.count.toLocaleString("fi-FI")}</TableCell>
            </TableRow>
          ))}
          {(summary?.transitions.length ?? 0) === 0 && (
            <TableRow>
              <TableCell colSpan={3}>
                <Typography color="text.secondary">Ei siirtymädataa vielä.</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Stack>
  );
};
